import React, { useCallback } from 'react';
import type { FC } from 'react';

import cn from 'classnames';

import { UserRole } from '@shared/types/user_role';

import { Roles } from '@admin/components/helpers/roles';
import { client } from '@admin/libraries/apollo';
import {
  AutoDispatchDisabledRoutesExistDocument,
  DispatchManageButtonEnabledDocument,
  Status,
  useAutoDispatchPermissionsUpdateMutation,
  useAutoDispatchToggleAllowedQuery,
} from '@admin/schema';

import { dispatchLayoutRefresh } from './dispatcher_editor_utils';

type DispatchToggleTarget = {
  id?: number | string | null;
  auto_dispatch_enabled?: boolean | null;
  lock_version?: number | null;
};

export const AutoDispatchEnabledCheckbox: FC<{
  dispatch: DispatchToggleTarget;
  regionID: number | string;
  date: Date | string;
  /** When true, toggle is draft-only until Save (Cancel reverts). */
  modifying?: boolean;
}> = ({ dispatch, regionID, date, modifying = false }) => {
  const dateString =
    typeof date === 'string'
      ? date.slice(0, 10)
      : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const autoDispatchDisabled = dispatch.auto_dispatch_enabled === false;
  const regionIDString = String(regionID);

  const { data: toggleData } = useAutoDispatchToggleAllowedQuery({
    client,
    variables: { regionID: regionIDString, date: dateString },
    skip: !regionID || !dateString,
  });

  const [execute, { loading }] = useAutoDispatchPermissionsUpdateMutation({
    client,
    refetchQueries: [
      { query: DispatchManageButtonEnabledDocument, variables: { regionID: regionIDString, date: dateString } },
      { query: AutoDispatchDisabledRoutesExistDocument, variables: { regionID: regionIDString, date: dateString } },
    ],
  });

  const toggleAllowed = toggleData?.autoDispatchToggleAllowed ?? false;

  const persist = useCallback(
    async (nextDisabled: boolean) => {
      const nextEnabled = !nextDisabled;
      dispatch.auto_dispatch_enabled = nextEnabled;
      dispatchLayoutRefresh();
      if (!dispatch.id) {
        return;
      }

      const revert = (message: string) => {
        dispatch.auto_dispatch_enabled = !nextEnabled;
        dispatchLayoutRefresh();
        window.alert(message);
      };

      try {
        const result = await execute({
          variables: { id: String(dispatch.id), autoDispatchEnabled: nextEnabled },
        });
        const payload = result.data?.result;
        if (payload?.status !== Status.Ok) {
          revert(payload?.error || 'Unable to update auto-dispatch.');
          return;
        }

        if (typeof dispatch.lock_version === 'number') {
          dispatch.lock_version += 1;
        }
      } catch (error) {
        revert(error instanceof Error ? error.message : 'Unable to update auto-dispatch.');
      }
    },
    [dispatch, execute],
  );

  const applyLocalToggle = (nextDisabled: boolean) => {
    dispatch.auto_dispatch_enabled = !nextDisabled;
    dispatchLayoutRefresh();
  };

  const onToggle = () => {
    const nextDisabled = !autoDispatchDisabled;
    if (loading) {
      return;
    }

    if (modifying) {
      applyLocalToggle(nextDisabled);
      return;
    }

    void persist(nextDisabled);
  };

  // After T+3 / publish handoff the lock UI is hidden; ops edit freely until dispatch day.
  if (!toggleAllowed) {
    return null;
  }

  return (
    <Roles show={[UserRole.Admin]}>
      <div className="dispatcher-auto-dispatch-enabled">
        <button
          type="button"
          role="switch"
          aria-checked={autoDispatchDisabled}
          aria-label="Disable auto-dispatch for route"
          disabled={loading}
          className={cn('dispatcher-auto-dispatch-switch', {
            'dispatcher-auto-dispatch-switch--on': autoDispatchDisabled,
          })}
          onClick={onToggle}
        >
          <span className="dispatcher-auto-dispatch-switch-knob" aria-hidden />
          {autoDispatchDisabled ? <span className="dispatcher-auto-dispatch-switch-label">Locked</span> : null}
        </button>
        <span className="dispatcher-auto-dispatch-enabled-label">Disable Auto-Dispatch for Route</span>
      </div>
    </Roles>
  );
};
